/**
 * 역할별 설정 및 권한 관리 유틸리티
 * 
 * 역할별로 다른 UI, 메뉴, 기능을 관리하기 위한 중앙 집중식 설정
 */

export type UserRole = 'saas_admin' | 'admin' | 'user';

export interface RoleConfig {
  // 사용자 관리 탭 설정
  userManagement: {
    showPendingTab: boolean;
    showCustomersTab: boolean;
    showAllUsersTab: boolean;
    allUsersTabLabel: string;
    customersTabLabel: string;
    autoSelectCustomer: boolean; // 기업관리자의 경우 자동으로 자신의 고객사 선택
  };
  
  // 기능별 권한
  permissions: {
    canManageCustomers: boolean; // 고객사 관리 (슈퍼관리자 전용)
    canManageServices: boolean; // 서비스 관리 (슈퍼관리자 전용)
    canManageBilling: boolean; // 결제 및 구독 관리
    canViewAllUsers: boolean; // 전체 사용자 조회 (슈퍼관리자 전용)
    canViewAllCustomers: boolean; // 전체 고객사 조회 (슈퍼관리자 전용)
    canEditUsers: boolean; // 사용자 수정 권한
    canInviteUsers: boolean; // 사용자 초대 권한
    canDeleteUsers: boolean; // 사용자 삭제 권한
  };
}

const roleConfigs: Record<UserRole, RoleConfig> = {
  // 슈퍼관리자: 모든 권한 + 슈퍼관리자 전용 메뉴 접근
  saas_admin: {
    userManagement: {
      showPendingTab: true,
      showCustomersTab: true,
      showAllUsersTab: true,
      allUsersTabLabel: '전체 사용자',
      customersTabLabel: '고객사의 사용자',
      autoSelectCustomer: false,
    },
    permissions: {
      canManageCustomers: true,
      canManageServices: true,
      canManageBilling: true,
      canViewAllUsers: true,
      canViewAllCustomers: true,
      canEditUsers: true,
      canInviteUsers: true,
      canDeleteUsers: true,
    },
  },
  // 기업관리자: 자신의 기업 내 관리 권한 (슈퍼관리자의 기업용 메뉴와 동일)
  admin: {
    userManagement: {
      showPendingTab: true,
      showCustomersTab: false, // 기업관리자는 고객사 선택 불필요
      showAllUsersTab: true, // 기업 내 전체 사용자
      allUsersTabLabel: '전체사용자',
      customersTabLabel: '전체사용자',
      autoSelectCustomer: true, // 자동으로 자신의 고객사 선택
    },
    permissions: {
      canManageCustomers: false,
      canManageServices: false,
      canManageBilling: true,
      canViewAllUsers: true, // 자신의 기업 내 사용자만
      canViewAllCustomers: false,
      canEditUsers: true,
      canInviteUsers: true,
      canDeleteUsers: true,
    },
  },
  // 일반 이용자: 읽기 전용 또는 제한된 권한
  user: {
    userManagement: {
      showPendingTab: false,
      showCustomersTab: false,
      showAllUsersTab: false,
      allUsersTabLabel: '전체 사용자',
      customersTabLabel: '고객사의 사용자',
      autoSelectCustomer: false,
    },
    permissions: {
      canManageCustomers: false,
      canManageServices: false,
      canManageBilling: false,
      canViewAllUsers: false,
      canViewAllCustomers: false,
      canEditUsers: false, // 일반 이용자는 수정 불가 (읽기 전용)
      canInviteUsers: false,
      canDeleteUsers: false,
    },
  },
};

/**
 * 역할에 따른 설정 가져오기
 */
export const getRoleConfig = (role: UserRole): RoleConfig => {
  return roleConfigs[role] || roleConfigs.user;
};

/**
 * 역할이 특정 권한을 가지고 있는지 확인
 */
export const hasPermission = (role: UserRole, permission: keyof RoleConfig['permissions']): boolean => {
  const config = getRoleConfig(role);
  return config.permissions[permission];
};

/**
 * 사용자의 권한(permission)에 따라 읽기 전용 여부 확인
 * 일반 이용자(user)는 기본적으로 읽기 전용이며, permission이 'readonly'인 경우도 읽기 전용
 */
export const isReadOnlyUser = (user: { role: UserRole; permission?: string }): boolean => {
  if (user.role === 'user') {
    return true; // 일반 이용자는 기본적으로 읽기 전용
  }
  if (user.permission === 'readonly') {
    return true; // permission이 readonly인 경우 읽기 전용
  }
  return false;
};

